

/**
 * Gera cores baseadas em quantis (divisão em grupos de tamanho similar)
 * @param {Array} cidadesOrdenadas - Array de [cidade, valor] ordenado por valor
 * @param {number} numQuantiles - Número de quantis a dividir (padrão: 5)
 * @returns {Object} Objeto com cidade como chave e valor de cor (51-255) como valor
 */
export function generateQuantileColors(cidadesOrdenadas, numQuantiles = 5) {
  if (cidadesOrdenadas.length === 0) return {};
  
  const valores = cidadesOrdenadas.map(([_, valor]) => valor).sort((a, b) => a - b);
  const quantiles = [];
  
  for (let i = 1; i <= numQuantiles; i++) {
    const index = Math.ceil((i / numQuantiles) * valores.length) - 1;
    quantiles.push(valores[index]);
  }
  
  let redScale = {};
  
  for (const [cidadeSemUF, valor] of cidadesOrdenadas) {
    let quantileIndex = quantiles.findIndex(q => valor <= q);
    if (quantileIndex === -1) quantileIndex = quantiles.length - 1;
    
    const color = Math.round(51 + (quantileIndex / (numQuantiles - 1)) * (255 - 51));
    redScale[cidadeSemUF] = color;
  }
  
  return redScale;
}

/**
 * Gera cores baseadas em escala linear (proporcional ao valor máximo)
 * @param {Array} cidadesOrdenadas - Array de [cidade, valor] ordenado por valor
 * @returns {Object} Objeto com cidade como chave e valor de cor (1-255) como valor
 */
export function generateLinearColors(cidadesOrdenadas) {
  if (cidadesOrdenadas.length === 0) return {};
  
  const max = cidadesOrdenadas[0][1];
  let redScale = {};
  
  for (const [cidadeSemUF, valor] of cidadesOrdenadas) {
    let color = Math.round((valor / max) * 255);
    if(color === 0) color = 1;
    redScale[cidadeSemUF] = color;
  }
  
  return redScale;
}

/**
 * Gera cores baseadas em escala logarítmica
 * @param {Array} cidadesOrdenadas - Array de [cidade, valor] ordenado por valor
 * @returns {Object} Objeto com cidade como chave e valor de cor (1-255) como valor
 */
export function generateLogColors(cidadesOrdenadas) {
  if (cidadesOrdenadas.length === 0) return {};
  
  const valores = cidadesOrdenadas.map(([_, valor]) => valor);
  const minValue = Math.min(...valores);
  const maxValue = Math.max(...valores);
  
  const minLog = Math.log(minValue + 1);
  const maxLog = Math.log(maxValue + 1);
  
  let redScale = {};
  
  for (const [cidadeSemUF, valor] of cidadesOrdenadas) {
    const logValue = Math.log(valor + 1);
    const normalizedValue = (logValue - minLog) / (maxLog - minLog);
    
    const color = Math.round(51 + normalizedValue * (255 - 51));
    redScale[cidadeSemUF] = Math.max(1, color);
  }
  
  return redScale;
}

/**
 * Gera cores baseadas em Z-Score (desvio padrão)
 * @param {Array} cidadesOrdenadas - Array de [cidade, valor] ordenado por valor
 * @returns {Object} Objeto com cidade como chave e valor de cor (1-255) como valor
 */
export function generateZScoreColors(cidadesOrdenadas) {
  if (cidadesOrdenadas.length === 0) return {};
  
  const valores = cidadesOrdenadas.map(([_, valor]) => valor);
  const media = valores.reduce((a, b) => a + b, 0) / valores.length;
  const desvioPadrao = Math.sqrt(valores.reduce((a, b) => a + Math.pow(b - media, 2), 0) / valores.length);
  
  let redScale = {};
  
  for (const [cidadeSemUF, valor] of cidadesOrdenadas) {
    const zScore = (valor - media) / desvioPadrao;
    
    const normalizedZ = Math.max(-2, Math.min(2, zScore));
    
    const color = Math.round(((normalizedZ + 2) / 4) * 255);
    redScale[cidadeSemUF] = Math.max(1, color);
  }
  
  return redScale;
}

/**
 * Função principal para gerar cores baseada no tipo de escala
 * @param {Array} cidadesOrdenadas - Array de [cidade, valor] ordenado por valor
 * @param {string} scaleType - Tipo de escala: 'linear', 'quantile', 'log', 'zscore'
 * @returns {Object} Objeto com cidade como chave e valor de cor como valor
 */
export function generateColors(cidadesOrdenadas, scaleType) {
  if (cidadesOrdenadas.length === 0) return {};
  
  switch (scaleType) {
    case 'quantile':
      return generateQuantileColors(cidadesOrdenadas, 5);
    case 'log':
      return generateLogColors(cidadesOrdenadas);
    case 'zscore':
      return generateZScoreColors(cidadesOrdenadas);
    default: 
      return generateLinearColors(cidadesOrdenadas);
  }
}

